import { QueryClientProvider } from '@tanstack/react-query';
import { ReactNode, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { Toaster } from '@/shared/components/ui/toaster';
import { queryClient } from '@/shared/lib/queryClient';
import { monitoringService } from '@/shared/services';
import { ThemeProvider } from './ThemeProvider';

interface AppProviderProps {
  readonly children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const initAuthListener = useAuthStore((state) => state.initAuthListener);

  // Initialize monitoring and authentication on app startup
  useEffect(() => {
    // Initialize monitoring first (must happen before any errors occur)
    monitoringService.initialize();

    // Subscribe to live Supabase auth-state changes BEFORE the session check,
    // so the store mirrors background token refresh, cross-tab sign-in/out and
    // the `PASSWORD_RECOVERY` event the SDK emits while it initializes. The
    // listener attaches asynchronously either way — `checkAuth` remains the
    // primary path — but subscribing first costs nothing and is deterministic.
    const unsubscribe = initAuthListener();

    // Then check authentication
    void checkAuth();

    return unsubscribe;
  }, [checkAuth, initAuthListener]);

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          {children}
          <Toaster />
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
